import React, { useState } from "react"
import { manageFacilityUpdate } from "../../../../api/manage_facility_update"
import { ManageFacilityData } from "../"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

interface ManageFacilityUpdateProps {
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
  facilityData: ManageFacilityData // 編集するサービス提供元データをプロパティとして受け取る
}

const ManageFacilityUpdate: React.FC<ManageFacilityUpdateProps> = ({ onClose, facilityData }) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    id: facilityData.id,
    name: facilityData.name,
    address: facilityData.address,
    phone: facilityData.phone,
    buildingType: facilityData.building_type
  })

  const [errors, setErrors] = useState({
    id: "",
    name: "",
    address: "",
    phone: "",
    buildingType: "",
    server: ""
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement // 明示的に型キャスト
    const { name, value, type, checked } = target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const validate = () => {
    const validationErrors = { ...errors }
    let hasError = false

    // 必須項目のバリデーション
    if (!formData.name) {
      validationErrors.name = "必須項目が入力されていません"
      hasError = true
    } else if (formData.name.length > 64) {
      validationErrors.name = "入力文字数は64文字以内です"
      hasError = true
    } else {
      validationErrors.name = ""
    }

    if (!formData.address) {
      validationErrors.address = "必須項目が入力されていません"
      hasError = true
    } else if (formData.address.length > 256) {
      validationErrors.address = "入力文字数は256文字以内です"
      hasError = true
    } else {
      validationErrors.address = ""
    }

    if (formData.phone && formData.phone.length > 14) {
      validationErrors.phone = "入力文字数は14文字以内です"
      hasError = true
    } else {
      validationErrors.phone = ""
    }

    if (formData.buildingType === undefined || formData.buildingType === null) {
      validationErrors.buildingType = "建物種別を選択してください"
      hasError = true
    } else {
      validationErrors.buildingType = ""
    }

    validationErrors.server = ""

    setErrors(validationErrors)
    return !hasError
  }

  const convertEnabledValues = (data: any) => {
    return data
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (validate()) {
      // バリデーション成功時の処理
      try {
        startLoading()
        const convertedData = convertEnabledValues(formData)
        await manageFacilityUpdate(
          convertedData.id,
          convertedData.name,
          convertedData.address,
          convertedData.phone,
          convertedData.buildingType
        )
        onClose(1)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/index") // セッション切れ時にトップページへリダイレクト
        } else if (error.response) {
          const errorDetail = error.response.data.detail
          const validationErrors = {
            id: "",
            name: "",
            address: "",
            phone: "",
            buildingType: "",
            server: ""
          }
          validationErrors.server = errorDetail.message
          setErrors(validationErrors)
        } else {
          const validationErrors = {
            id: "",
            name: "",
            address: "",
            phone: "",
            buildingType: "",
            server: ""
          }
          validationErrors.server = "サーバーへの接続に失敗しました"
          setErrors(validationErrors)
        }
      } finally {
        stopLoading()
      }
    }
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "8px 16px",
            boxSizing: "border-box"
          }}
        >
          <MDTypography variant="h4" fontWeight="medium" style={{ flex: 1, textAlign: "left" }}>
            施設を更新
          </MDTypography>
          <button
            className="close-button"
            onClick={() => onClose(0)}
            style={{
              position: "absolute",
              right: 16,
              top: 8,
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer"
            }}
          >
            ×
          </button>
        </div>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="施設ID"
              type="text"
              name="id"
              value={formData.id}
              onChange={handleChange}
              sx={{ width: "100%" }}
              inputProps={{ readOnly: true }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="施設名"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.name && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.name}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="住所"
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.address && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.address}
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="電話番号"
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              sx={{ width: "100%" }}
            />
          </MDBox>
          {errors.phone && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.phone}
            </MDBox>
          )}
          <div>
            <select
              name="buildingType"
              value={formData.buildingType}
              onChange={handleChange}
              className={formData.buildingType === -1 ? "placeholder" : ""}
            >
              <option value={-1} hidden>
                建物種別を選択してください
              </option>
              <option value={0}>マンション</option>
              <option value={1}>アパート</option>
            </select>
          </div>
          {errors.buildingType && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.buildingType}
            </MDBox>
          )}
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              更新
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageFacilityUpdate
